import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getClasses, createClass, deleteClass, generateLessons, replaceCurriculum, ClassOut } from "../api/client";
import { FULL_CURRICULUM } from "../data/lessonPresets";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildSchedule(days: string[], time: string): string {
  if (days.length === 0) return "";
  const parts = days.join(" & ");
  return time ? `${parts} ${time}` : parts;
}

function parseDaysFromSchedule(schedule: string): string[] {
  if (!schedule) return [];
  return DAYS.filter((d) => schedule.toLowerCase().includes(d.toLowerCase()));
}

function parseTimeFromSchedule(schedule: string): string {
  if (!schedule) return "";
  const match = schedule.match(/(\d{1,2}:\d{2}\s*(am|pm)?|\d{1,2}\s*(am|pm))/i);
  return match ? match[0] : "";
}

export default function Classes() {
  const [classes, setClasses] = useState<ClassOut[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState("");

  const load = () => getClasses(false).then((r) => setClasses(r.data));
  useEffect(() => { load(); }, []);

  const toggleDay = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    const schedule = buildSchedule(selectedDays, selectedTime);
    const r = await createClass({ ...form, schedule });
    const classId = r.data?.id;
    if (classId) {
      // Always load full curriculum (Intro + Intermediate + Advanced)
      try {
        await replaceCurriculum(classId, FULL_CURRICULUM);
      } catch (e) {}
      // Auto-generate lessons for 6 months if schedule is set
      if (selectedDays.length > 0) {
        const today = new Date();
        const sixMonths = new Date(today);
        sixMonths.setMonth(sixMonths.getMonth() + 6);
        try {
          await generateLessons({
            class_id: classId,
            start_date: today.toISOString().split("T")[0],
            end_date: sixMonths.toISOString().split("T")[0],
          });
        } catch (e) {}
      }
    }
    setForm({ name: "", description: "" });
    setSelectedDays([]);
    setSelectedTime("");
    setShowModal(false);
    load();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete class "${name}" and all its sessions/assignments?`)) return;
    await deleteClass(id);
    load();
  };

  return (
    <div>
      <div className="toolbar">
        <h2>Classes</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Class</button>
      </div>

      <div className="table-container">
        {classes.length === 0 ? (
          <div className="empty">No classes yet. Create one to get started.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Schedule</th><th>Students</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/classes/${c.id}`} style={{ color: "var(--accent)", fontWeight: 500 }}>{c.name}</Link></td>
                  <td>{c.schedule || "-"}</td>
                  <td>{c.student_count}</td>
                  <td><span className={`badge ${c.active ? "present" : "absent"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id, c.name)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Class</h3>
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Math Grade 10" />
            </div>
            <div className="form-group">
              <label>Days</label>
              <div style={{ display: "flex", gap: 6 }}>
                {DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    style={{
                      flex: 1,
                      padding: "8px 0",
                      borderRadius: 6,
                      border: selectedDays.includes(day) ? "2px solid var(--accent)" : "1px solid var(--border)",
                      background: selectedDays.includes(day) ? "var(--accent-light)" : "var(--bg-card)",
                      color: selectedDays.includes(day) ? "var(--accent-text)" : "var(--text-secondary)",
                      fontWeight: selectedDays.includes(day) ? 600 : 400,
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Time</label>
              <input type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
